import React from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import { Edit16Regular } from '@fluentui/react-icons';

// Invoice payment-terms options. Keys mirror the backend
// (pdf/serializers.PAYMENT_TERMS_LABELS) — the dropdown persists the key onto
// the order; the PDF/serializer renders the matching label. Default: due_on_receipt.
export const PAYMENT_TERMS_OPTIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'due_on_receipt', label: 'Due on receipt' },
  { key: 'net_15', label: 'Net 15' },
  { key: 'net_30', label: 'Net 30' },
];
export const DEFAULT_PAYMENT_TERMS = 'due_on_receipt';
const paymentTermsLabel = (key?: string) =>
  PAYMENT_TERMS_OPTIONS.find((o) => o.key === key)?.label ??
  PAYMENT_TERMS_OPTIONS.find((o) => o.key === DEFAULT_PAYMENT_TERMS)!.label;

interface DocumentPreviewProps {
  documentType: 'invoice' | 'contract' | 'quote';
  documentData: {
    number: string;
    customer: string;
    customerEmail?: string;
    customerPhone?: string;
    customerAddress?: string;
    billingAddress?: string;
    /** Bill To address (multi-line, newline-separated). Rendered as a
     *  labeled block under the customer contact info. */
    billToAddress?: string;
    /** Ship To address (multi-line). Rendered only when different from
     *  billToAddress to avoid duplicating the same block. */
    shipToAddress?: string;
    companyName: string;
    companyAddress: string;
    companyPhone: string;
    companyEmail: string;
    companyLogo?: string;
    date: string;
    order: string;
    pickup: string;
    return: string;
    items: Array<{
      quantity: number;
      name: string;
      duration: string;
      price: number;
      tax?: number;
      total: number;
      /** Free-text per-line note (Booqable extra_information), under the name. */
      extraDetails?: string;
    }>;
    subtotal: number;
    totalInclTaxes: number;
    paid?: number;
    /** Balance due, already NET of any PR withholding (the caller subtracts it,
     *  mirroring the react-pdf serializer's toBePaidInCents). */
    due?: number;
    /** PR services-withholding held back by the customer (invoices) — a positive
     *  magnitude in CENTS, rendered as a below-the-line "−$X" deduction above
     *  Due. Omitted / 0 when no holdback applies. */
    prWithholdingInCents?: number;
    /** Rate label for the withholding row, e.g. "10%". */
    prWithholdingRate?: string;
    /** Invoice payment-terms key (e.g. 'net_30'). Seeds the terms dropdown. */
    paymentTerms?: string;
    signature?: string;
    /** Real-life project name (orders.project_name) — its own meta row when set. */
    projectName?: string;
    // Government / procurement fields — render only when populated.
    contractNumber?: string;
    poNumber?: string;
    solicitationNumber?: string;
    authorizedOfficialName?: string;
    authorizedOfficialTitle?: string;
    contractorUei?: string;
    contractorTaxId?: string;
  };
}

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '900px',
    width: '90vw',
    maxHeight: '90vh',
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
  },
  documentContainer: {
    position: 'relative',
    backgroundColor: '#ffffff',
    color: '#1a1a1a',
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontSize: '14px',
    lineHeight: 1.5,
    ...shorthands.padding('56px', '64px'),
  },
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    ...shorthands.gap('40px'),
    marginBottom: '24px',
  },
  partyBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  partyName: {
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '2px',
  },
  labelBlock: {
    marginTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  labelStrong: {
    fontWeight: 700,
    color: '#1a1a1a',
  },
  // Letterhead band — logo over the abstract brand wash + a navy bottom rule,
  // mirroring the react-pdf DocumentLayout header band (HeaderBackground +
  // COLOR_BRAND_NAVY #305367, 2.5pt bottom border).
  letterhead: {
    position: 'relative',
    ...shorthands.overflow('hidden'),
    ...shorthands.padding('10px', '10px', '12px', '10px'),
    marginBottom: '20px',
    ...shorthands.borderBottom('2px', 'solid', '#305367'),
  },
  // Abstract brand fill painted behind the logo — same gradient wash + diagonal
  // accents as website HeaderBackground.tsx, as a stretched inline SVG.
  letterheadBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
    pointerEvents: 'none',
  },
  logoImage: {
    position: 'relative',
    zIndex: 1,
    maxHeight: '64px',
    maxWidth: '240px',
    objectFit: 'contain',
    objectPosition: 'left center',
    display: 'block',
  },
  metaTable: {
    marginTop: '20px',
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: '16px',
    rowGap: '4px',
  },
  metaLabel: {
    fontWeight: 700,
    color: '#1a1a1a',
  },
  metaValue: {
    color: '#1a1a1a',
  },
  // Inline-editable field — renders as plain document text until the user
  // clicks the pencil; no persistent underline. Used only when the `editable`
  // prop is set (e.g. the /documents/:id preview); read-only callers never
  // see it.
  // A transparent border + padding are reserved in BOTH modes so flipping to
  // edit mode only recolors the border — the box size never changes, so the
  // pencil icon beside it doesn't jump.
  editable: {
    outlineStyle: 'none',
    ...shorthands.border('1px', 'solid', 'transparent'),
    ...shorthands.borderRadius('3px'),
    ...shorthands.padding('0', '3px'),
  },
  // Applied only while actively editing — recolors the reserved border blue.
  editableActive: {
    ...shorthands.borderColor('#0f6cbd'),
    cursor: 'text',
  },
  editableBlock: {
    display: 'block',
    whiteSpace: 'pre-line',
    minWidth: '120px',
    minHeight: '60px',
  },
  // Wraps an editable field + its pencil affordance so they sit together
  // inline without disrupting the surrounding document text flow.
  editWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  editIcon: {
    color: '#0f6cbd',
    fontSize: '14px',
    cursor: 'pointer',
    flexShrink: 0,
    transitionProperty: 'color',
    transitionDuration: '120ms',
    ':hover': {
      color: '#0a4d8c',
    },
  },
  headline: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginTop: '24px',
    marginBottom: '20px',
  },
  headlineNumber: {
    fontWeight: 700,
  },
  itemsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '8px',
  },
  colHeader: {
    color: '#7a7a7a',
    fontWeight: 400,
    fontSize: '13px',
    textAlign: 'right',
    ...shorthands.padding('0', '4px', '8px', '4px'),
  },
  colHeaderLeft: {
    color: '#7a7a7a',
    fontWeight: 400,
    fontSize: '13px',
    textAlign: 'left',
    ...shorthands.padding('0', '4px', '8px', '4px'),
  },
  itemRow: {
    ...shorthands.borderTop('1px', 'solid', '#e5e5e5'),
  },
  itemRowLast: {
    ...shorthands.borderBottom('1px', 'solid', '#e5e5e5'),
  },
  itemCell: {
    ...shorthands.padding('12px', '4px'),
    verticalAlign: 'top',
  },
  nameCell: {
    fontWeight: 700,
    color: '#1a1a1a',
  },
  // Inline variant / rental-period label after the item name — mirrors the
  // react-pdf DataRow which flows the variant inline after the title.
  itemVariant: {
    fontWeight: 400,
    color: '#7a7a7a',
  },
  // Free-text per-line note (Booqable extra_information), rendered under the
  // item name — mirrors the react-pdf cellExtraDetails caption. preserveWhite
  // keeps any newlines the admin typed.
  itemExtraDetails: {
    fontWeight: 400,
    color: '#7a7a7a',
    fontSize: '12px',
    marginTop: '2px',
    whiteSpace: 'pre-wrap',
  },
  // Rental-cycle column cell — the per-line charge period in days (e.g.
  // "7 days"); muted and normal-weight, em-dash when the line has no period.
  rentalCell: {
    fontWeight: 400,
    color: '#7a7a7a',
    whiteSpace: 'nowrap',
  },
  qtyCell: {
    width: '60px',
    textAlign: 'right',
    color: '#1a1a1a',
    whiteSpace: 'nowrap',
  },
  priceCell: {
    textAlign: 'right',
    width: '100px',
    paddingRight: '4px',
    whiteSpace: 'nowrap',
  },
  amountCell: {
    textAlign: 'right',
    width: '100px',
    whiteSpace: 'nowrap',
  },
  totalsWrap: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  totalsBlock: {
    minWidth: '320px',
    display: 'flex',
    flexDirection: 'column',
  },
  totalRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    columnGap: '32px',
    ...shorthands.padding('6px', '0'),
  },
  totalLabel: {
    textAlign: 'right',
    color: '#1a1a1a',
  },
  totalValue: {
    textAlign: 'right',
    color: '#1a1a1a',
    minWidth: '90px',
  },
  totalRowGrand: {
    ...shorthands.borderTop('1px', 'solid', '#d4d4d4'),
    ...shorthands.padding('12px', '0'),
    fontWeight: 700,
  },
  totalRowDue: {
    ...shorthands.borderTop('1px', 'solid', '#d4d4d4'),
    ...shorthands.padding('12px', '0'),
    fontWeight: 700,
  },
  signatureSection: {
    marginTop: '48px',
    paddingTop: '20px',
    ...shorthands.borderTop('1px', 'solid', '#e5e5e5'),
  },
  signatureLabel: {
    fontWeight: 700,
    marginBottom: '10px',
  },
  signatureImage: {
    height: '80px',
    marginTop: '10px',
  },
  authSection: {
    marginTop: '48px',
    paddingTop: '24px',
    ...shorthands.borderTop('1px', 'solid', '#e5e5e5'),
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '340px',
  },
  authLabel: {
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '8px',
  },
  // Signature + seal sit on one row, baseline-aligned, like the react-pdf
  // signature block (whose signature image embeds the seal).
  authSignatureRow: {
    display: 'flex',
    alignItems: 'flex-end',
    ...shorthands.gap('16px'),
    marginBottom: '4px',
  },
  authSignatureImage: {
    maxHeight: '64px',
    maxWidth: '200px',
    objectFit: 'contain',
    objectPosition: 'left bottom',
    pointerEvents: 'none',
    userSelect: 'none',
  },
  authSealImage: {
    width: '88px',
    height: '88px',
    objectFit: 'contain',
    pointerEvents: 'none',
    userSelect: 'none',
  },
  officialName: {
    fontWeight: 700,
    color: '#1a1a1a',
  },
  officialTitle: {
    color: '#7a7a7a',
  },
  watermark: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-25deg)',
    fontSize: '140px',
    fontWeight: 800,
    color: 'rgba(34, 139, 34, 0.08)',
    pointerEvents: 'none',
    textTransform: 'uppercase',
    letterSpacing: '8px',
    zIndex: 0,
  },
});

const titleFor = (documentType: 'invoice' | 'contract' | 'quote') => {
  if (documentType === 'invoice') return 'Invoice';
  if (documentType === 'contract') return 'Contract';
  return 'Quote';
};

const fmtMoney = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Fields the preview can expose for inline editing (one-off PDF overrides).
export type EditableField =
  | 'number'
  | 'date'
  | 'customer'
  | 'customerEmail'
  | 'customerPhone'
  | 'customerAddress'
  | 'billToAddress';

export type DocumentFieldOverrides = Partial<Record<EditableField, string>>;

// Map inline edits → the whitelisted DocumentData patch the website react-pdf
// route merges onto the canonical data. Only fields the user actually edited
// are sent; money / totals are never overridable. Returns a base64url string
// (URL-safe across the Flask → Next hop, UTF-8 safe for non-ASCII PR customer
// names), or '' when nothing was edited. Co-located with EditableField so the
// field set and its DocumentData mapping stay in one place.
export const buildPdfOverrideParam = (overrides: DocumentFieldOverrides): string => {
  const patch: Record<string, any> = {};
  const to: Record<string, string> = {};
  if (overrides.number !== undefined) patch.number = overrides.number;
  if (overrides.date !== undefined) {
    const d = new Date(overrides.date);
    if (!isNaN(d.getTime())) patch.issueDate = d.toISOString().slice(0, 10);
  }
  if (overrides.customer !== undefined) to.name = overrides.customer;
  if (overrides.customerEmail !== undefined) to.email = overrides.customerEmail;
  if (overrides.customerPhone !== undefined) to.phone = overrides.customerPhone;
  if (overrides.customerAddress !== undefined) {
    // The PDF header prefers billToAddress over the structured party address,
    // so set both: addressLine1 (structured fallback) and billToAddress (what
    // actually renders when present).
    to.addressLine1 = overrides.customerAddress;
    patch.billToAddress = overrides.customerAddress;
  }
  // An explicit Bill To edit wins over the address-derived one.
  if (overrides.billToAddress !== undefined) patch.billToAddress = overrides.billToAddress;
  if (Object.keys(to).length) patch.to = to;
  if (!Object.keys(patch).length) return '';
  const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(patch))));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

// Inline-editable text node with a pencil affordance. The field reads as plain
// document text and only becomes editable when the user clicks the pencil; on
// blur it commits and reverts to display mode. Uncontrolled by design: it seeds
// from `initial` once and reports edits on blur, so React never rewrites the
// node mid-edit (which would jump the caret). `multiline` reads innerText to
// preserve the user's line breaks for address blocks.
const EditableText: React.FC<{
  initial: string;
  field: EditableField;
  className: string;
  multiline?: boolean;
  onCommit?: (field: EditableField, value: string) => void;
}> = ({ initial, field, className, multiline, onCommit }) => {
  const styles = useStyles();
  const ref = React.useRef<HTMLSpanElement>(null);
  const [editing, setEditing] = React.useState(false);

  // Focus + drop the caret at the end once the node turns editable.
  React.useEffect(() => {
    if (!editing || !ref.current) return;
    ref.current.focus();
    const range = document.createRange();
    range.selectNodeContents(ref.current);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [editing]);

  return (
    <span className={styles.editWrap}>
      <span
        ref={ref}
        role="textbox"
        tabIndex={editing ? 0 : -1}
        contentEditable={editing}
        suppressContentEditableWarning
        className={editing ? mergeClasses(className, styles.editableActive) : className}
        onBlur={(e) => {
          const next = multiline
            ? e.currentTarget.innerText
            : e.currentTarget.textContent ?? '';
          setEditing(false);
          if (next !== initial) onCommit?.(field, next);
        }}
      >
        {initial}
      </span>
      <Edit16Regular
        className={styles.editIcon}
        aria-label="Edit"
        onClick={() => setEditing(true)}
      />
    </span>
  );
};

export const DocumentPreviewContent: React.FC<{
  documentType: 'invoice' | 'contract' | 'quote';
  documentData: DocumentPreviewProps['documentData'];
  /** When true, customer block / document number / date render as inline
   *  editable fields. Edits are reported via onFieldChange (the caller turns
   *  them into one-off PDF overrides). Defaults to read-only. */
  editable?: boolean;
  onFieldChange?: (field: EditableField, value: string) => void;
  /** When provided (invoices), the Payment terms row renders as a dropdown and
   *  the chosen term key is reported here — the owner PERSISTS it onto the
   *  order (unlike inline edits, which are one-off overrides). Absent → the
   *  terms render read-only. */
  onPaymentTermsChange?: (termKey: string) => void;
}> = ({ documentType, documentData, editable, onFieldChange, onPaymentTermsChange }) => {
  const styles = useStyles();

  // Payment-terms selection (invoices). Seeded from the persisted order value;
  // kept in local state so the dropdown updates instantly, then reconciled when
  // the refetched document flows back through props.
  const [paymentTerm, setPaymentTerm] = React.useState<string>(
    documentData.paymentTerms || DEFAULT_PAYMENT_TERMS,
  );
  React.useEffect(() => {
    setPaymentTerm(documentData.paymentTerms || DEFAULT_PAYMENT_TERMS);
  }, [documentData.paymentTerms]);

  const subtotal = documentData.subtotal;
  const total = documentData.totalInclTaxes;
  const derivedTax = Math.max(0, +(total - subtotal).toFixed(2));

  // PR services-withholding — held back by the customer and remitted to
  // Hacienda; recognized revenue (Total) is unchanged, only Due drops. Computed
  // on the pre-tax subtotal, so the row sits with the pre-tax totals (above the
  // tax line) to make the base unambiguous. Mirrors the react-pdf TotalsBlock.
  const withholdingRow =
    documentData.prWithholdingInCents !== undefined &&
    documentData.prWithholdingInCents > 0 ? (
      <div className={styles.totalRow}>
        <div className={styles.totalLabel}>
          PR withholding
          {documentData.prWithholdingRate ? ` (${documentData.prWithholdingRate})` : ''}
        </div>
        <div className={styles.totalValue}>
          −{fmtMoney(documentData.prWithholdingInCents / 100)}
        </div>
      </div>
    ) : null;

  // Recipient block mirrors the react-pdf invoice Header: ONE bill-to block,
  // not a floating customer block repeated under a separate "Bill To". The
  // bill-to snapshot leads with the recipient name (already shown bold above),
  // so drop that first line to avoid printing the name twice — and for a
  // company+contact, Booqable's snapshot keeps the contact line, which now
  // shows once under the company name. Falls back to the structured address
  // when there's no snapshot.
  const nameNorm = (documentData.customer || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const billToAddressLines = (documentData.billToAddress ? documentData.billToAddress.split('\n') : [])
    .map((line) => line.trim())
    .filter(Boolean)
    .filter(
      (line, i) =>
        !(i === 0 && nameNorm && line.toLowerCase().replace(/[^a-z0-9]/g, '').startsWith(nameNorm)),
    );
  const billToAddressText = billToAddressLines.length
    ? billToAddressLines.join('\n')
    : documentData.customerAddress || '';

  const isPaid =
    documentType === 'invoice' &&
    documentData.paid !== undefined &&
    documentData.paid > 0 &&
    (documentData.due === undefined || documentData.due === 0);

  return (
    <div className={styles.documentContainer}>
      {isPaid && <div className={styles.watermark}>Paid</div>}

      <div className={styles.letterhead}>
        {/* Abstract brand wash — same gradient + diagonal accents as the
            react-pdf HeaderBackground, drawn as a stretched inline SVG. */}
        <svg
          className={styles.letterheadBg}
          viewBox="0 0 612 90"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="twmHeaderFade" x1="0" y1="0" x2="1" y2="0.6">
              <stop offset="0" stopColor="#305367" stopOpacity="0.12" />
              <stop offset="1" stopColor="#305367" stopOpacity="0" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="612" height="90" fill="url(#twmHeaderFade)" />
          <polygon points="612,0 612,90 462,0" fill="#305367" fillOpacity="0.06" />
          <polygon points="612,90 612,45 522,90" fill="#305367" fillOpacity="0.05" />
        </svg>
        <img
          src={documentData.companyLogo || '/logo-tradewinds.png'}
          alt={documentData.companyName}
          className={styles.logoImage}
        />
      </div>

      <div className={styles.header}>
        <div className={styles.partyBlock}>
          <div className={styles.partyName}>{documentData.companyName}</div>
          {documentData.companyAddress && (
            <div style={{ whiteSpace: 'pre-line' }}>{documentData.companyAddress}</div>
          )}
          {documentData.companyPhone && <div>{documentData.companyPhone}</div>}
          {documentData.companyEmail && (
            <div style={{ marginTop: '8px' }}>{documentData.companyEmail}</div>
          )}
          {/* Federal contractor identifier — government/procurement invoices
              only. Mirrors the react-pdf Header company block. */}
          {documentData.contractorUei && (
            <div style={{ marginTop: '8px' }}>UEI: {documentData.contractorUei}</div>
          )}

          <div className={styles.metaTable}>
            <div className={styles.metaLabel}>
              {documentType === 'invoice' ? 'Invoice date' : 'Date'}
            </div>
            {/* Read-only: the date is a passthrough artifact (the document's
                created date), never an edited field. */}
            <div className={styles.metaValue}>{documentData.date}</div>

            <div className={styles.metaLabel}>Order</div>
            {/* Order number is minted at reserve; a pre-reserve (draft) order has
                none yet. Show "Pending" rather than "#N/A" so staff don't mistake
                it for a real (e.g. Booqable) number. */}
            <div className={styles.metaValue}>
              {documentData.order && documentData.order !== 'N/A'
                ? `#${documentData.order}`
                : 'Pending'}
            </div>

            <div className={styles.metaLabel}>From</div>
            <div className={styles.metaValue}>{documentData.pickup}</div>

            <div className={styles.metaLabel}>Until</div>
            <div className={styles.metaValue}>{documentData.return}</div>

            {documentType === 'invoice' && (
              <>
                <div className={styles.metaLabel}>Payment terms</div>
                <div className={styles.metaValue}>
                  {onPaymentTermsChange ? (
                    <Dropdown
                      size="small"
                      value={paymentTermsLabel(paymentTerm)}
                      selectedOptions={[paymentTerm]}
                      onOptionSelect={(_, d) => {
                        const key = d.optionValue;
                        if (!key) return;
                        setPaymentTerm(key);
                        onPaymentTermsChange(key);
                      }}
                    >
                      {PAYMENT_TERMS_OPTIONS.map((o) => (
                        <Option key={o.key} value={o.key}>
                          {o.label}
                        </Option>
                      ))}
                    </Dropdown>
                  ) : (
                    paymentTermsLabel(paymentTerm)
                  )}
                </div>
              </>
            )}

            {documentData.contractNumber && (
              <>
                <div className={styles.metaLabel}>Contract #</div>
                <div className={styles.metaValue}>{documentData.contractNumber}</div>
              </>
            )}
            {documentData.poNumber && (
              <>
                <div className={styles.metaLabel}>PO #</div>
                <div className={styles.metaValue}>{documentData.poNumber}</div>
              </>
            )}
            {documentData.solicitationNumber && (
              <>
                <div className={styles.metaLabel}>Solicitation #</div>
                <div className={styles.metaValue}>{documentData.solicitationNumber}</div>
              </>
            )}
          </div>
        </div>

        <div className={styles.partyBlock}>
          <div className={styles.partyName}>
            {editable ? (
              <EditableText
                initial={documentData.customer}
                field="customer"
                className={styles.editable}
                onCommit={onFieldChange}
              />
            ) : (
              documentData.customer
            )}
          </div>

          {/* Real-life project name — directly below the customer name, bold to
              match it, above the billing address. */}
          {documentData.projectName && (
            <div className={styles.partyName}>{documentData.projectName}</div>
          )}

          {/* Single recipient address block. In edit mode this is the
              billToAddress override field (the structured customer address
              isn't populated on documents); display mode shows the snapshot
              with the leading name line stripped. */}
          {editable ? (
            <div>
              <EditableText
                initial={billToAddressText}
                field="billToAddress"
                className={`${styles.editable} ${styles.editableBlock}`}
                multiline
                onCommit={onFieldChange}
              />
            </div>
          ) : billToAddressText ? (
            <div style={{ whiteSpace: 'pre-line' }}>{billToAddressText}</div>
          ) : null}

          {/* Contact rows render their inline-edit affordance only when the
              field has a value — a blank field with a lone pencil (e.g. a
              customer with no phone on file) reads as a stray, clickable icon
              under the email, so suppress it entirely when empty. */}
          {documentData.customerEmail ? (
            editable ? (
              <div>
                <EditableText
                  initial={documentData.customerEmail}
                  field="customerEmail"
                  className={styles.editable}
                  onCommit={onFieldChange}
                />
              </div>
            ) : (
              <div>{documentData.customerEmail}</div>
            )
          ) : null}

          {documentData.customerPhone ? (
            editable ? (
              <div>
                <EditableText
                  initial={documentData.customerPhone}
                  field="customerPhone"
                  className={styles.editable}
                  onCommit={onFieldChange}
                />
              </div>
            ) : (
              <div>{documentData.customerPhone}</div>
            )
          ) : null}

          {/* Ship To only when the delivery address differs from the bill-to —
              the standard invoice pattern (no second copy for individuals). */}
          {documentData.shipToAddress &&
            documentData.shipToAddress !== documentData.billToAddress && (
              <div className={styles.labelBlock}>
                <div className={styles.labelStrong}>Service Address</div>
                <div style={{ whiteSpace: 'pre-line' }}>{documentData.shipToAddress}</div>
              </div>
            )}
        </div>
      </div>

      <h1 className={styles.headline}>
        {titleFor(documentType)}{' '}
        {documentType === 'invoice' && documentData.number === 'Draft invoice' ? (
          // Unfinalized invoice — no official number yet. Read "Invoice Draft"
          // rather than "Invoice #Draft invoice".
          <span className={styles.headlineNumber}>Draft</span>
        ) : (
          <span className={styles.headlineNumber}>
            #
            {/* Invoice number is the TWM order number (lockstep) — a passthrough,
                never editable. Quotes/contracts keep their editable doc number. */}
            {editable && documentType !== 'invoice' ? (
              <EditableText
                initial={documentData.number}
                field="number"
                className={styles.editable}
                onCommit={onFieldChange}
              />
            ) : (
              documentData.number
            )}
          </span>
        )}
      </h1>

      <table className={styles.itemsTable}>
        <thead>
          <tr>
            <th className={styles.colHeaderLeft}>Item</th>
            <th className={styles.colHeaderLeft}>Rental cycle</th>
            <th className={styles.colHeader}>Qty</th>
            <th className={styles.colHeader}>Price</th>
            <th className={styles.colHeader}>Total</th>
          </tr>
        </thead>
        <tbody>
          {documentData.items.map((item, index) => {
            const isLast = index === documentData.items.length - 1;
            const rowClass = isLast
              ? `${styles.itemRow} ${styles.itemRowLast}`
              : styles.itemRow;
            return (
              <tr key={index} className={rowClass}>
                <td className={`${styles.itemCell} ${styles.nameCell}`}>
                  {item.name}
                  {item.extraDetails ? (
                    <div className={styles.itemExtraDetails}>{item.extraDetails}</div>
                  ) : null}
                </td>
                <td className={`${styles.itemCell} ${styles.rentalCell}`}>
                  {item.duration || '—'}
                </td>
                <td className={`${styles.itemCell} ${styles.qtyCell}`}>
                  {item.quantity}
                </td>
                <td className={`${styles.itemCell} ${styles.priceCell}`}>
                  {fmtMoney(item.price)}
                </td>
                <td className={`${styles.itemCell} ${styles.amountCell}`}>
                  {fmtMoney(item.total)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className={styles.totalsWrap}>
        <div className={styles.totalsBlock}>
          {/* Subtotal rows only when taxed — mirrors the react-pdf TotalsBlock,
              which collapses to a single "Total" for untaxed docs. */}
          {derivedTax > 0 && (
            <>
              <div className={styles.totalRow}>
                <div className={styles.totalLabel}>Subtotal</div>
                <div className={styles.totalValue}>{fmtMoney(subtotal)}</div>
              </div>
              <div className={styles.totalRow}>
                <div className={styles.totalLabel}>Total excl. taxes</div>
                <div className={styles.totalValue}>{fmtMoney(subtotal)}</div>
              </div>
              {withholdingRow}
              <div className={styles.totalRow}>
                <div className={styles.totalLabel}>IVU (11.5%)</div>
                <div className={styles.totalValue}>{fmtMoney(derivedTax)}</div>
              </div>
            </>
          )}
          {/* Untaxed docs have no pre-tax group above; keep the holdback with the
              totals (above the grand total), not down in the payment section. */}
          {derivedTax <= 0 && withholdingRow}
          <div className={`${styles.totalRow} ${styles.totalRowGrand}`}>
            <div className={styles.totalLabel}>
              {derivedTax > 0 ? 'Total incl. taxes' : 'Total'}
            </div>
            <div className={styles.totalValue}>{fmtMoney(total)}</div>
          </div>
          {documentData.paid !== undefined && documentData.paid > 0 && (
            <div className={styles.totalRow}>
              <div className={styles.totalLabel}>Paid</div>
              <div className={styles.totalValue}>{fmtMoney(documentData.paid)}</div>
            </div>
          )}
          {documentData.due !== undefined && (
            <div className={`${styles.totalRow} ${styles.totalRowDue}`}>
              <div className={styles.totalLabel}>Due</div>
              <div className={styles.totalValue}>{fmtMoney(documentData.due)}</div>
            </div>
          )}
        </div>
      </div>

      {(documentType === 'contract' || documentType === 'quote') && (
        <div className={styles.signatureSection}>
          <div className={styles.signatureLabel}>Signature</div>
          {documentData.signature && (
            <div className={styles.signatureImage}>{documentData.signature}</div>
          )}
        </div>
      )}

      {/* Authorized Official — signature + company seal sit together at the
          bottom, mirroring the react-pdf DocumentLayout (where the seal is part
          of the signature block, not a separate floating stamp). */}
      {documentType === 'invoice' && documentData.authorizedOfficialName && (
        <div className={styles.authSection}>
          <div className={styles.authLabel}>Authorized Official</div>
          <div className={styles.authSignatureRow}>
            <img
              src="/damian-signature.png"
              alt={`${documentData.authorizedOfficialName} signature`}
              className={styles.authSignatureImage}
            />
            <img
              src="/TWM-stamp.png"
              alt="Tradewinds Mechanical LLC seal"
              className={styles.authSealImage}
            />
          </div>
          <div className={styles.officialName}>{documentData.authorizedOfficialName}</div>
          {documentData.authorizedOfficialTitle && (
            <div className={styles.officialTitle}>{documentData.authorizedOfficialTitle}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentPreviewContent;
